import type { OnboardingGuidanceStep } from '../../types';

/**
 * Resolves a step's active variant by DOM presence.
 *
 * Steps may declare `variants` — alternate step definitions that replace the base step when their
 * anchor element (`data-guidance-step="flowName:variantSelector"`) is currently in the DOM. Pages
 * opt in to a variant by rendering that anchor attribute only when the variant applies, so the
 * page state that decides the variant and the element the popover anchors to can never disagree.
 *
 * Returns the first variant whose anchor exists, or the base step when none match (or the step has
 * no variants). Callers should resolve whenever the step changes; the result is only as fresh as
 * the DOM at call time.
 */
export function resolveGuidanceStep(flowName: string, step: OnboardingGuidanceStep): OnboardingGuidanceStep {
  if (!step.variants?.length || typeof document === 'undefined') {
    return step;
  }

  for (const variant of step.variants) {
    if (!variant.selector) {
      continue;
    }
    const compoundSelector = `${flowName}:${variant.selector}`;
    if (document.querySelector(`[data-guidance-step="${CSS.escape(compoundSelector)}"]`)) {
      return variant;
    }
  }

  return step;
}
